import React, { useContext, useEffect, useState } from 'react';
import styles from './ProductOverview.module.scss';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import { Spin } from 'antd';
import { initSocket } from 'shared/utils/initSocket';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import EmissionProvider from 'shared/components/EmissionProvider';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { BadgeColors } from 'enums/COBadgeEnums';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as EditIcon } from 'assets/icons/draft.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as VerifiedIcon } from 'assets/icons/verified.svg';
import SubmitApprovalModal from 'shared/components/SubmitApprovalModal';
import isProductDisabled from 'shared/utils/isProductDisabled';
import { ProductsEnums } from 'enums/ProductsEnums';
import RejectReasonModal from 'shared/components/Products/DeclinedProducts/RejectReasonModal';
import { SocketContext } from 'context/SocketContext';
import { TabsKeys } from 'enums/ProductsViewEnums';
import clsx from 'clsx';
import ProductSiblingService from 'services/ProductSiblingsService';
import { navigateProductOverview } from './navigateToProductStage';
import { SubmitApprovalModalViewProps } from 'shared/components/SubmitApprovalModal/SubmitApprovalConstants';
import ProductOverviewContent from './ProductOverviewContent';
import DashboardService from 'services/DashboardService/dashboard.service';
import ProductCreationService from 'services/ProductsService/productCreation.service';
import EmissionService from 'services/ProductsService/emission.service';
import { useUserRole } from 'shared/hooks/useUserRole';
import { canEdit } from 'shared/utils/roleUtils';

const ProductOverview = () => {
  const navigate = useNavigate();
  let productId: string | undefined;
  const location = useLocation();
  const { id } = useParams();
  const userRole = useUserRole();
  const canEditProduct = canEdit(userRole);

  productId = id;
  let inCreationPhase = location.state?.inCreationPhase === true ? true : false;
  const {
    fetchProductEmissionsStage1AndStage2,
    fetchProductEmissionsStage3,
    fetchProductEmissionsStage4,
    stage1Emissions,
    stage2Emissions,
    stage3Emissions,
    stage4Emissions,
    stage1EmissionsTotal,
    stage2EmissionsTotal,
    stage3EmissionsTotal,
    stage4EmissionsTotal,
    checkTotalEmissionStatus,
  } = EmissionService();
  const {
    productOverviewLoader,
    setProductOverviewLoader,
    productLoading,
    fetchSingleProduct,
    newProduct,
  } = ProductCreationService();
  const { viewApprovalReport } = DashboardService();
  const { fetchProductSiblings, productSiblingsOptions } =
    ProductSiblingService();

  const currentStage = newProduct?.stage;

  const { HEADING, CHECKBOX_CONTENT, CONTENT } = SubmitApprovalModalViewProps;

  const [showRejectReason, setShowRejectReason] = useState<boolean>(false);

  const { setSocketConnection, socket } = useContext(SocketContext);

  useEffect(() => {
    const token = localStorage.getItem('user');
    socket?.disconnect();
    const socketDetails = initSocket(token);
    setSocketConnection(socketDetails);
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('emission-calculation', () => {
        fetchProductEmissionsStage1AndStage2(productId);
        fetchProductEmissionsStage3(productId);
        fetchProductEmissionsStage4(productId);
      });

      return () => {
        socket.off('emission-calculation', () => {});
      };
    }
  }, [socket]);

  useEffect(() => {
    if (!productLoading) fetchSingleProduct(Number(productId));
    productId && fetchProductSiblings(Number(productId));
  }, [productId, socket]);

  useEffect(() => {
    if (!newProduct) return;

    if (newProduct.status === 'draft') {
      checkTotalEmissionStatus();
    } else {
      fetchProductEmissionsStage1AndStage2(productId);
      fetchProductEmissionsStage3(productId);
      fetchProductEmissionsStage4(productId);
    }

    if (newProduct.status === 'declined' && location.state?.fromNotification) {
      setShowRejectReason(true);
    }
  }, [newProduct]);

  const isDisabled = isProductDisabled(newProduct?.status);

  const handleGoBack = () => {
    if (inCreationPhase) {
      navigate(
        generatePath(
          `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_PRODUCT_DISTRIBUTION}`,
          { id: id }
        )
      );
    } else if (!inCreationPhase) {
      navigate(-1);
    }
  };

  const handleViewMoreNavigation = () => {
    navigate(
      generatePath(
        `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_RAW_MATERIAL}`,
        { id: id }
      )
    );
  };
  const handleDownloadNavigation = () =>
    navigate(generatePath(AppRoutes.DOWNLOAD_MAP, { id: id }));
  const handleReport = () => viewApprovalReport(productId);

  const handleEditNavigation = () => {
    navigate(
      generatePath(
        `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_RAW_MATERIAL}`,
        { id: id }
      )
    );
  };

  const handleCloseButton = () => {
    const status = newProduct?.status;

    switch (status) {
      case TabsKeys.IN_REVIEW:
        navigate(`${AppRoutes.PRODUCT}/${AppRoutes.IN_REVIEW}`);
        break;

      case TabsKeys.APPROVED:
        navigate(`${AppRoutes.PRODUCT}/${AppRoutes.APPROVED}`);
        break;

      case TabsKeys.DRAFT:
        navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);
        break;

      case TabsKeys.DECLINED:
        navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DECLINED}`);
        break;
    }
  };

  const [approvalModalState, setApprovalModalState] = useState<boolean>(false);
  const handleOpenModal = () => {
    setApprovalModalState(true);
  };
  const handleCloseModal = () => {
    setApprovalModalState(false);
  };
  const getBadgeDetails = (status: string | undefined) => {
    let badgeColor = BadgeColors.BLUE;
    let icon;
    switch (status) {
      case ProductsEnums.DRAFT:
        badgeColor = BadgeColors.BLUE;
        icon = <EditIcon className={styles.draftIconBlue} />;
        break;

      case ProductsEnums.IN_REVIEW:
        badgeColor = BadgeColors.ORANGE;
        icon = <SearchIcon className={styles.inReviewIconYellow} />;
        break;

      case ProductsEnums.APPROVED:
        badgeColor = BadgeColors.DARKGREEN;
        icon = <VerifiedIcon className={styles.verifiedIconGreen} />;
        break;

      case ProductsEnums.DECLINED:
        badgeColor = BadgeColors.RED;
        icon = <CloseIcon className={styles.declinedIconRed} />;
        break;
    }

    return { badgeColor, icon };
  };

  const { badgeColor, icon } = getBadgeDetails(newProduct?.status);
  //TODO dummy handler no use with this
  const handleReportingSubmit = () => {};
  const handleReportingYearChange = async (value: string) => {
    const productData = await fetchSingleProduct(Number(value));
    navigateProductOverview(
      productData?.id,
      productData?.stage,
      productData?.status,
      navigate
    );
  };

  return (
    <EmissionProvider>
      <DashboardContainer noPadding>
        <div
          className={clsx(
            productOverviewLoader && styles.loaderOverviewContainer,
            styles.overviewConatiner
          )}>
          {productOverviewLoader && (
            <div className={styles.loaderContainer}>
              <Spin spinning />
            </div>
          )}
          <ProductOverviewContent
            newProduct={newProduct}
            productId={productId}
            currentStage={currentStage}
            stage1EmissionsTotal={stage1EmissionsTotal}
            stage2EmissionsTotal={stage2EmissionsTotal}
            stage3EmissionsTotal={stage3EmissionsTotal}
            stage4EmissionsTotal={stage4EmissionsTotal}
            stage1Emissions={stage1Emissions}
            stage2Emissions={stage2Emissions}
            stage3Emissions={stage3Emissions}
            stage4Emissions={stage4Emissions}
            productSiblingsOptions={productSiblingsOptions}
            isDisabled={isDisabled}
            inCreationPhase={inCreationPhase}
            badgeColor={badgeColor}
            icon={icon}
            canEditProduct={canEditProduct}
            handleGoBack={handleGoBack}
            handleCloseButton={handleCloseButton}
            handleDownloadNavigation={handleDownloadNavigation}
            handleReport={handleReport}
            handleViewMoreNavigation={handleViewMoreNavigation}
            handleEditNavigation={handleEditNavigation}
            handleOpenModal={handleOpenModal}
            setShowRejectReason={setShowRejectReason}
            handleReportingYearChange={handleReportingYearChange}
            setProductOverviewLoader={setProductOverviewLoader}
          />
        </div>
      </DashboardContainer>
      <SubmitApprovalModal
        showModalState={approvalModalState}
        handleModalCancel={handleCloseModal}
        handleModalConfirm={handleCloseModal}
        productId={productId}
        heading={HEADING}
        content={CONTENT}
        confirmationText={CHECKBOX_CONTENT}
      />
      <RejectReasonModal
        productDetails={newProduct}
        showModal={showRejectReason}
        hideModal={() => setShowRejectReason(false)}
        handleViewProduct={() => setShowRejectReason(false)}
      />
    </EmissionProvider>
  );
};

export default ProductOverview;
